import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { MediumFormatting } from "@faire/web--source/common/localization/formatting";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackFaireDirectCreditHcArticleFdLandingPageView } from "@faire/web-api--source/events/faireDirect/view/creditHcArticleFdLandingPage";
import brandPath from "@faire/web-api--source/routes/brand/brandToken";
import helpCenterArticles from "@faire/web-api--source/routes/support/articles/articleId";
import { observer } from "mobx-react";
import * as React from "react";
import { useEffect } from "react";
import styled from "styled-components";
import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { getIsBrandPreview } from "@faire/retailer-visitor-shared/lib/getIsBrandPreview";
import { getExternalSalesRepName } from "@faire/retailer/serialized-data/getExternalSalesRepName";
import { getSettingFaireDirectCreditExpiresInDays } from "@faire/retailer/settings/getSettingFaireDirectCreditExpiresInDays";
import { useSettingFirstOrderIncetiveForFaireDirectLoggedOut, useAssignSettingFirstOrderIncetiveForFaireDirectLoggedOut } from "@faire/retailer/settings/useSettingFirstOrderIncetiveForFaireDirectLoggedOut";
import { SignIn } from "@faire/retailer/views/faire-direct/components/SignIn";
import { NarrowContainer } from "../components/CommonStyles";
import { FaireAndBrandLogo } from "../components/FaireAndBrandLogo";
import { SignUpButton } from "../components/SignUpButton";
var IntroSectionBase = function(param) {
    var brand = param.brand, isPreSample = param.isPreSample, innerRef = param.innerRef, loginUrl = param.loginUrl;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var viewport = useViewport();
    var signUpSection = /*#__PURE__*/ _jsx(Column, {
        align: "center",
        ref: innerRef,
        children: /*#__PURE__*/ _jsx(SignUpButton, {
            primary: true,
            "data-test-id": "faire-direct-sign-up-button-main"
        })
    });
    var externalSalesRepName = getExternalSalesRepName();
    var _useFaireDirectFirstOrderIncentive = useFaireDirectFirstOrderIncentive(), isFaireDirectCreditPercentageBased = _useFaireDirectFirstOrderIncentive.isFaireDirectCreditPercentageBased, formattedMaxPercentageDiscountAmount = _useFaireDirectFirstOrderIncentive.formattedMaxPercentageDiscountAmount;
    var faireDirectBrandToken = useFaireDirectBrand().faireDirectBrandToken;
    var getGreetingContent = function() {
        if (externalSalesRepName) {
            return strictLocalize({
                id: "lLVX8H",
                defaultMessage: [
                    {
                        type: 1,
                        value: "salesRepName"
                    },
                    {
                        type: 0,
                        value: " invited you to shop "
                    },
                    {
                        type: 1,
                        value: "brandName"
                    },
                    {
                        type: 0,
                        value: " on Faire!"
                    }
                ]
            }, DEPRECATED_backwardsCompatibleValues({
                salesRepName: externalSalesRepName,
                brandName: brand.name
            }));
        }
        if (!isFaireDirectCreditPercentageBased) {
            return strictLocalize({
                id: "FefIHw",
                defaultMessage: [
                    {
                        type: 0,
                        value: "We\u2019ve partnered with Faire to sell our wholesale products online."
                    }
                ]
            });
        }
        return null;
    };
    var getDisclaimer = function() {
        if (isFaireDirectCreditPercentageBased) {
            var _brand_name;
            return /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "DvCGzs",
                defaultMessage: [
                    {
                        type: 0,
                        value: "*Available for retailers who are new to Faire. Maximum discount of "
                    },
                    {
                        type: 1,
                        value: "maximumDiscountMoney"
                    },
                    {
                        type: 0,
                        value: " and free shipping with "
                    },
                    {
                        type: 8,
                        value: "BrandPageLinkFormatting",
                        children: [
                            {
                                type: 1,
                                value: "brandCompanyName"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: " only. Expires "
                    },
                    {
                        type: 1,
                        value: "expiresInDaysNumber"
                    },
                    {
                        type: 0,
                        value: " days after retailer signs up for Faire."
                    }
                ],
                values: {
                    brandCompanyName: (_brand_name = brand.name) !== null && _brand_name !== void 0 ? _brand_name : "",
                    maximumDiscountMoney: formattedMaxPercentageDiscountAmount,
                    expiresInDaysNumber: getSettingFaireDirectCreditExpiresInDays(),
                    BrandPageLinkFormatting: function(chunks) {
                        return BrandPageLinkFormatting(chunks, faireDirectBrandToken);
                    }
                }
            });
        }
        if (brand.name) {
            return /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "G1C6bu",
                defaultMessage: [
                    {
                        type: 0,
                        value: "*You must be new to Faire and meet the minimum for "
                    },
                    {
                        type: 8,
                        value: "BrandPageLinkFormatting",
                        children: [
                            {
                                type: 1,
                                value: "brandCompanyName"
                            }
                        ]
                    },
                    {
                        type: 0,
                        value: ". No other conditions apply. "
                    },
                    {
                        type: 8,
                        value: "LinkFormatting",
                        children: [
                            {
                                type: 0,
                                value: "Learn More"
                            }
                        ]
                    }
                ],
                values: {
                    brandCompanyName: brand.name,
                    LinkFormatting: HelpCenterLinkFormatting,
                    BrandPageLinkFormatting: function(chunks) {
                        return BrandPageLinkFormatting(chunks, faireDirectBrandToken);
                    }
                }
            });
        } else {
            return /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "AYN8qp",
                defaultMessage: [
                    {
                        type: 0,
                        value: "*You must be new to Faire and meet the minimum for the brand. No other conditions apply. "
                    },
                    {
                        type: 8,
                        value: "LinkFormatting",
                        children: [
                            {
                                type: 0,
                                value: "Learn More"
                            }
                        ]
                    }
                ],
                values: {
                    LinkFormatting: HelpCenterLinkFormatting
                }
            });
        }
    };
    var _brand_name;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            viewport.isMobile && !getIsBrandPreview() && !isPreSample ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(MobileSignInContainer, {
                        children: /*#__PURE__*/ _jsx(CenterRow, {
                            children: /*#__PURE__*/ _jsx(SignIn, {
                                loginUrl: loginUrl,
                                eventPath: "fd-landing-page/click/log-in"
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(HorizontalLine, {})
                ]
            }) : null,
            /*#__PURE__*/ _jsx(Container, {
                children: /*#__PURE__*/ _jsxs(IntroContainer, {
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: viewport.isMobile ? "3x" : "8x"
                        }),
                        /*#__PURE__*/ _jsx(FaireAndBrandLogo, {
                            brandName: brand.name || ""
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "3x"
                        }),
                        viewport.isMobile ? /*#__PURE__*/ _jsx(Typography, {
                            variant: "displayMSerifRegular",
                            align: "center",
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "OOrMZf",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Shop "
                                    },
                                    {
                                        type: 1,
                                        value: "givenCompanyName"
                                    },
                                    {
                                        type: 0,
                                        value: " online"
                                    }
                                ],
                                values: {
                                    givenCompanyName: (_brand_name = brand.name) !== null && _brand_name !== void 0 ? _brand_name : ""
                                }
                            })
                        }) : /*#__PURE__*/ _jsx(Typography, {
                            variant: "displayLSerifRegular",
                            align: "center",
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "mKx4bw",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Shop "
                                    },
                                    {
                                        type: 8,
                                        value: "NonBreakingFormatting",
                                        children: [
                                            {
                                                type: 1,
                                                value: "brandName"
                                            }
                                        ]
                                    },
                                    {
                                        type: 0,
                                        value: " online"
                                    }
                                ],
                                values: DEPRECATED_backwardsCompatibleValues({
                                    brandName: brand.name,
                                    NonBreakingFormatting: NonBreakingFormatting
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsxs(InnerContainer, {
                            children: [
                                /*#__PURE__*/ _jsx(Spacer, {
                                    size: "2x"
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: {
                                        mobileAndAbove: "paragraphSansRegular",
                                        tabletAndAbove: "sectionHeaderSansMedium"
                                    },
                                    align: "center",
                                    children: getGreetingContent()
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: {
                                        mobileAndAbove: "paragraphSansRegular",
                                        tabletAndAbove: "sectionHeaderSansMedium"
                                    },
                                    align: "center",
                                    children: /*#__PURE__*/ _jsx(DiscountMessageContent, {})
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    size: {
                                        mobileAndAbove: 24,
                                        tabletAndAbove: 40
                                    }
                                }),
                                signUpSection,
                                /*#__PURE__*/ _jsx(Spacer, {
                                    size: {
                                        mobileAndAbove: 16,
                                        tabletAndAbove: 28
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "labelSansRegular",
                                    color: Core.text.subdued,
                                    align: "center",
                                    children: getDisclaimer()
                                }),
                                viewport.isMobile ? /*#__PURE__*/ _jsx(Spacer, {
                                    size: "3x"
                                }) : null
                            ]
                        })
                    ]
                })
            })
        ]
    });
};
export var IntroSection = observer(IntroSectionBase);
var DiscountMessageContent = function() {
    var _useFaireDirectFirstOrderIncentive = useFaireDirectFirstOrderIncentive(), isFaireDirectCreditPercentageBased = _useFaireDirectFirstOrderIncentive.isFaireDirectCreditPercentageBased, formattedMaxPercentageDiscountAmount = _useFaireDirectFirstOrderIncentive.formattedMaxPercentageDiscountAmount, formattedFixedBasedAmount = _useFaireDirectFirstOrderIncentive.formattedFixedBasedAmount, formattedPercentageBasedNumber = _useFaireDirectFirstOrderIncentive.formattedPercentageBasedNumber;
    var isFoiForFD = useSettingFirstOrderIncetiveForFaireDirectLoggedOut();
    var assignFoiForFD = useAssignSettingFirstOrderIncetiveForFaireDirectLoggedOut();
    useEffect(function() {
        assignFoiForFD();
    }, [
        assignFoiForFD
    ]);
    if (isFoiForFD) {
        return isFaireDirectCreditPercentageBased ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "Agr/Su",
            defaultMessage: [
                {
                    type: 0,
                    value: "Join today to claim your referral offer: "
                },
                {
                    type: 1,
                    value: "faireDirectPercentageDiscountNumber"
                },
                {
                    type: 0,
                    value: " off your first order, up to "
                },
                {
                    type: 1,
                    value: "formattedMaxPercentageDiscountMoney"
                },
                {
                    type: 0,
                    value: ", and 1 year of free shipping.*"
                }
            ],
            values: {
                faireDirectPercentageDiscountNumber: formattedPercentageBasedNumber,
                formattedMaxPercentageDiscountMoney: formattedMaxPercentageDiscountAmount
            }
        }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "bGRC/m",
            defaultMessage: [
                {
                    type: 0,
                    value: "Join today to claim your referral offer: "
                },
                {
                    type: 8,
                    value: "MediumFormatting",
                    children: [
                        {
                            type: 1,
                            value: "discountMoney"
                        },
                        {
                            type: 0,
                            value: " off"
                        }
                    ]
                },
                {
                    type: 0,
                    value: " your first order, and "
                },
                {
                    type: 8,
                    value: "MediumFormatting",
                    children: [
                        {
                            type: 0,
                            value: "1 year of free shipping"
                        }
                    ]
                },
                {
                    type: 0,
                    value: ".*"
                }
            ],
            values: {
                MediumFormatting: MediumFormatting,
                discountMoney: formattedFixedBasedAmount
            }
        });
    }
    return isFaireDirectCreditPercentageBased ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "6psddx",
        defaultMessage: [
            {
                type: 0,
                value: "Join today and get "
            },
            {
                type: 1,
                value: "faireDirectPercentageDiscountNumber"
            },
            {
                type: 0,
                value: " off on your first order (up to "
            },
            {
                type: 1,
                value: "maximumDiscountMoney"
            },
            {
                type: 0,
                value: ") and 1 year of free shipping\u2014covered by Faire.*"
            }
        ],
        values: {
            faireDirectPercentageDiscountNumber: formattedPercentageBasedNumber,
            maximumDiscountMoney: formattedMaxPercentageDiscountAmount
        }
    }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "nanhDA",
        defaultMessage: [
            {
                type: 0,
                value: "Join today and get "
            },
            {
                type: 8,
                value: "MediumFormatting",
                children: [
                    {
                        type: 1,
                        value: "discountMoney"
                    },
                    {
                        type: 0,
                        value: " off"
                    }
                ]
            },
            {
                type: 0,
                value: " and "
            },
            {
                type: 8,
                value: "MediumFormatting",
                children: [
                    {
                        type: 0,
                        value: "1 year of free shipping"
                    }
                ]
            },
            {
                type: 0,
                value: ", covered by Faire.*"
            }
        ],
        values: {
            MediumFormatting: MediumFormatting,
            discountMoney: formattedFixedBasedAmount
        }
    });
};
var BrandPageLinkFormatting = function(chunks, brandToken) {
    return /*#__PURE__*/ _jsx(Link, {
        to: brandPath(brandToken !== null && brandToken !== void 0 ? brandToken : ""),
        children: chunks
    });
};
var HelpCenterLinkFormatting = function(chunks) {
    return /*#__PURE__*/ _jsx(EmbeddedLink, {
        linkTo: helpCenterArticles("360034300092"),
        onClick: trackFaireDirectCreditHcArticleFdLandingPageView,
        target: "_blank",
        children: chunks
    });
};
var NonBreakingFormatting = function(chunks) {
    return /*#__PURE__*/ _jsx("div", {
        style: {
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden"
        },
        children: chunks
    });
};
var MobileSignInContainer = styled.div.withConfig({
    componentId: "sc-6a1dfdf8-0"
})([
    "width:100%;background:",
    ";padding:9px 0;align-items:center;"
], Decorative.tint.neutral);
var Container = styled(Column).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-6a1dfdf8-1"
})([
    "width:100%;background:",
    ";padding:20px;padding-bottom:220px;"
], Decorative.tint.neutral);
var IntroContainer = styled(NarrowContainer).withConfig({
    componentId: "sc-6a1dfdf8-2"
})([
    "align-items:center;max-width:930px;"
]);
var InnerContainer = styled(Column).withConfig({
    componentId: "sc-6a1dfdf8-3"
})([
    "max-width:740px;padding:0 15px;"
]);
var CenterRow = styled(Row).withConfig({
    componentId: "sc-6a1dfdf8-4"
})([
    "align-self:center;margin:auto;width:fit-content;"
]);
